import { useEffect } from "react";
import { useDemoIdentity } from "../../../lib/DemoIdentityContext";
import { useTenantTerms } from "../../../lib/TenantConfigContext";
import { Btn, CARD, cx } from "../../../components/msd/ui";
import { Icon } from "../../../components/msd/Icon";

// Pieces the brand's Organization, People and Roles pages share.

/** Per page: whether this login may open it, and whether it may change it.
 *  `edit` is exactly what the old pages' readOnly flag said. */
export function useOrgAccess() {
  const { role } = useDemoIdentity();
  const { pageLevel, canSeePage } = useTenantTerms();
  const of = (page) => {
    const edit = pageLevel(role, page) === "edit";
    return { see: edit || !!canSeePage(role, page), edit };
  };
  return {
    regions: of("regions"),
    dealers: of("dealers"),
    workshops: of("workshops"),
    people: of("people"),
    roles: of("roles"),
  };
}

// .gico — the small coloured square an icon sits in
export function Gico({ icon, size = 30, radius = 8, iconSize = 15, bg, fg = "#fff" }) {
  return (
    <span
      className="flex shrink-0 items-center justify-center"
      style={{ width: size, height: size, borderRadius: radius, background: bg, color: fg }}
    >
      <Icon name={icon} size={iconSize} stroke={2} />
    </span>
  );
}

// .tg-list / .opt-list — rows in one rounded, raised list
export const TG_LIST = "flex flex-col overflow-hidden rounded-[14px] shadow-msd-e1 [&>*+*]:shadow-[0_-1px_0_var(--line)]";
export const TG_ROW = "flex items-center gap-3 bg-msd-surface px-[14px] py-3";

/** A .fl for a group of controls. Field is a <label>, which would hand a click
 *  anywhere in it to the first switch, so a list of them needs a plain box. */
export function FieldGroup({ label, error, hint, children }) {
  return (
    <div className="flex flex-col gap-[7px]" role="group" aria-label={label}>
      <span className="text-[13.5px] font-semibold text-msd-ink2">{label}</span>
      {children}
      {error ? (
        <span className="text-[12.5px] text-msd-brick-ink" role="alert">
          {error}
        </span>
      ) : hint ? (
        <span className="text-[12.5px] text-msd-muted">{hint}</span>
      ) : null}
    </div>
  );
}

export const plural =(n, one, many) => `${n} ${n === 1 ? one : many}`;

export function OrgLoading() {
  return <div className="px-1 py-6 text-[14px] text-msd-muted">Loading</div>;
}

/** A query that failed says so, with a retry, rather than spinning forever. */
export function OrgError({ error, onRetry }) {
  const detail = error?.response?.data?.detail || error?.message || "Something went wrong.";
  return (
    <div className={cx(CARD, "px-8 py-12 text-center")}>
      <div className="text-[16px] font-bold text-msd-ink">Couldn&apos;t load this</div>
      <p className="mx-auto mb-0 mt-2 max-w-[460px] text-[13px] text-msd-muted">{detail}</p>
      {onRetry ? (
        <Btn className="mt-[18px]" onClick={onRetry}>
          Try again
        </Btn>
      ) : null}
    </div>
  );
}

/** Confirmation before a delete. `blocked` turns it into an explanation of
 *  why it cannot happen yet, with no destructive button to press. Rendered in
 *  place (not portalled) so it keeps the `.msd` tokens. */
export function ConfirmDelete({ open, onClose, name, body, blocked, onConfirm, pending }) {
  useEffect(() => {
    if (!open) return undefined;
    const key = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", key);
    return () => document.removeEventListener("keydown", key);
  }, [open, onClose]);
  if (!open) return null;
  const title = blocked ? `Can't delete ${name}` : `Delete ${name}?`;
  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-[rgba(15,21,48,.18)]" onClick={onClose} />
      <div
        role="alertdialog"
        aria-modal="true"
        aria-label={title}
        className="relative flex w-full max-w-[420px] flex-col gap-3 rounded-[22px] bg-msd-surface p-[22px] text-msd-ink shadow-msd-pop"
      >
        <div className="text-[20px] font-bold tracking-[-0.015em]">{title}</div>
        <p className="m-0 text-[14px] text-msd-ink2">{blocked || body}</p>
        <div className="mt-2 flex justify-end gap-[10px]">
          <Btn onClick={onClose}>{blocked ? "Close" : "Cancel"}</Btn>
          {!blocked && (
            <Btn variant="red" onClick={onConfirm} disabled={pending}>
              Delete
            </Btn>
          )}
        </div>
      </div>
    </div>
  );
}

/** The red text button on the left of a sheet's footer. */
export function RemoveBtn({ children, ...rest }) {
  return (
    <Btn className="mr-auto !text-[#F04438]" {...rest}>
      {children}
    </Btn>
  );
}
